import { useState } from 'react';
import posthog from 'posthog-js';
import useMountEffect from '../../hooks/useMountEffect';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { AGENT_CHAT_FEATURE_FLAG } from '../../utils/EnvConfigUtils';

const readFlag = (): boolean => !!posthog.isFeatureEnabled(AGENT_CHAT_FEATURE_FLAG);

/**
 * Whether Oscar is on for this user — the `agent-chat` PostHog flag, the same
 * one that reveals the menu items in `EnvConfigUtils` — and buyer-only: the
 * agent can't act for a supplier yet. The flag alone isn't enough. It is
 * evaluated per person, so someone who is a buyer admin in one account would
 * also see Oscar in their supplier one.
 *
 * Read into state and subscribed, not read during render. `session_thunks`
 * renders the app before `ThirdPartyService.boot` calls `posthog.identify`, so
 * the first evaluation is anonymous — and if that came back false, nothing
 * re-rendered when the real flags landed. An enabled user simply never saw
 * Oscar until some unrelated update happened to refresh the component.
 */
const useAgentChatEnabled = (userType: string): boolean => {
	const [enabled, setEnabled] = useState(readFlag);

	useMountEffect(() => {
		setEnabled(readFlag());
		return posthog.onFeatureFlags(() => setEnabled(readFlag()));
	});

	return enabled && userType === ROLE_TYPES.BUYER;
};

export default useAgentChatEnabled;
